/**
 * <s-media-slider> — swipeable, one-at-a-time media slides.
 *
 * Markup: a horizontally scroll-snapping [data-slider-track] whose children are
 * slides, plus optional [data-slide-dot] buttons (with data-index), and
 * commandfor buttons anywhere on the page. A progress bar anywhere on the page
 * marked [data-slider-progress-for="<this id>"] gets --progress (0–1) set to the
 * share of slides seen. Swipe/scroll works natively; JS adds buttons and keeps
 * the dots' aria-current in sync with the visible slide.
 *
 * data-protect-media on the element blocks the right-click/long-press save menu and dragging on its media.
 *
 * Commands (via commandfor/command on buttons targeting this element):
 *   --previous / --next  move one slide (wrapping at the ends).
 */
class SMediaSlider extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', (event) => this.#onCommand(event), opts);
    this.querySelectorAll('[data-slide-dot]').forEach((dot) => {
      dot.addEventListener('click', () => this.#go(Number(dot.getAttribute('data-index')) || 0), opts);
    });
    this.#track()?.addEventListener('scroll', () => this.#sync(), { ...opts, passive: true });
    // data-protect-media: no right-click/long-press save menu or drag-out on the slides and thumbnails.
    if (this.hasAttribute('data-protect-media')) {
      const block = (/** @type {Event} */ event) => {
        if (event.target instanceof Element && event.target.closest('img, video, picture, [data-slider-track]')) event.preventDefault();
      };
      this.addEventListener('contextmenu', block, opts);
      this.addEventListener('dragstart', block, opts);
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** @returns {HTMLElement | null} */
  #track() {
    const track = this.querySelector('[data-slider-track]');
    return track instanceof HTMLElement ? track : null;
  }

  #count() {
    return this.#track()?.children.length ?? 0;
  }

  /** @param {Event & { command?: string }} event */
  #onCommand(event) {
    const count = this.#count();
    if (!count) return;
    if (event.command === '--next') this.#go((this.#index + 1) % count);
    if (event.command === '--previous') this.#go((this.#index - 1 + count) % count);
  }

  /** @param {number} index */
  #go(index) {
    const track = this.#track();
    if (!track) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: index * track.clientWidth * (rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
    this.#index = index;
    this.#paint();
  }

  #sync() {
    const track = this.#track();
    if (!track || !track.clientWidth) return;
    this.#index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#paint();
  }

  #paint() {
    const count = this.#count();
    if (this.id && count) {
      document.querySelectorAll(`[data-slider-progress-for="${CSS.escape(this.id)}"]`).forEach((bar) => {
        if (bar instanceof HTMLElement) bar.style.setProperty('--progress', String((this.#index + 1) / count));
      });
    }
    this.querySelectorAll('[data-slide-dot]').forEach((dot) => {
      if (Number(dot.getAttribute('data-index')) === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    [...(this.#track()?.children ?? [])].forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.#index);
    });
  }
}

if (!customElements.get('s-media-slider')) customElements.define('s-media-slider', SMediaSlider);
